import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { LuTriangleAlert, LuCircleCheck, LuCircle } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useOnboardingChecklist } from '../onboarding/OnboardingChecklistProvider';
import { buildNotificationStatusCopy } from './NotificationsRequiredBanner';
import { registerVapidSubscription } from '../push/registerVapidSubscription';
import { useInstallAppStatus } from '../hooks/useInstallAppStatus';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import InstallAppInstructions from './onboarding/InstallAppInstructions';

const TOTAL_ITEMS = 5;

function getStatusIcon(status) {
  switch (status) {
    case 'done':
      return { icon: LuCircleCheck, color: 'green.400' };
    case 'blocked':
      return { icon: LuTriangleAlert, color: 'orange.400' };
    default:
      return { icon: LuCircle, color: 'gray.400' };
  }
}

function focusElementById(elementId) {
  if (typeof document === 'undefined') return;
  if (!elementId) return;
  const node = document.getElementById(elementId);
  node?.focus?.();
}

function scrollToElement(elementId) {
  if (typeof document === 'undefined') return;
  if (!elementId) return;
  const node = document.getElementById(elementId);
  node?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

export default function TopBarCompletionRing({ condensed = false }) {
  const { state, refresh } = useOnboardingChecklist();
  const navigate = useNavigate();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [ctaLoadingKey, setCtaLoadingKey] = useState(null);
  const installContext = useInstallAppStatus();
  const { canPromptInstall, promptInstall } = useInstallPrompt();
  const { platform: installPlatform, isInstalled: isAppInstalled } = installContext;

  const ringTrackColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const neutralRingColor = useColorModeValue('blue.400', 'blue.300');
  const warningRingColor = useColorModeValue('orange.400', 'orange.300');
  const successRingColor = useColorModeValue('green.400', 'green.300');
  const popoverBg = 'bg.surface';
  const cardBorderColor = 'border.subtle';
  const doneRowBg = 'status.successBg';
  const todoRowBg = 'status.todoBg';
  const doneRowBorder = useColorModeValue('green.200', 'green.500');
  const todoRowBorder = cardBorderColor;
  const buttonSize = condensed ? 'xs' : 'sm';
  const buttonPaddingX = condensed ? 1.5 : 2;
  const buttonGap = condensed ? 1 : 2;
  const ringSize = condensed ? '32px' : '36px';
  const ringThickness = condensed ? '8px' : '10px';
  const ringLabelFontSize = condensed ? '9px' : '10px';

  const notificationCopy = useMemo(
    () => buildNotificationStatusCopy(state.notificationsPermission),
    [state.notificationsPermission]
  );

  const notificationsBlocked =
    state.notificationsPermission === 'unsupported' ||
    state.notificationsPermission === 'noSW';

  const navigateToSettings = useCallback(
    (elementId, focusId) => {
      setIsOpen(false);
      navigate('/settings');
      setTimeout(() => {
        scrollToElement(elementId);
        if (focusId) {
          focusElementById(focusId);
        }
      }, 300);
    },
    [navigate]
  );

  const handleEnableNotifications = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) {
      toast({
        title: 'Notifications not supported',
        status: 'error',
      });
      return;
    }
    if (!('serviceWorker' in navigator)) {
      toast({
        title: 'Service workers disabled',
        description: 'Enable service workers in your browser to receive notifications.',
        status: 'warning',
      });
      return;
    }

    setCtaLoadingKey('notificationsPermission');
    try {
      let permission = Notification.permission;
      if (permission !== 'granted') {
        permission = await Notification.requestPermission();
      }
      if (permission !== 'granted') {
        toast({
          title: 'Notifications still blocked',
          description: 'Allow notifications in your browser settings to continue.',
          status: 'warning',
        });
        return;
      }

      await registerVapidSubscription();
      toast({ title: 'Notifications enabled', status: 'success' });
      refresh();
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to enable notifications', err);
      toast({
        title: 'Could not enable notifications',
        status: 'error',
      });
    } finally {
      setCtaLoadingKey(null);
    }
  }, [refresh, toast]);

  const goToComposer = useCallback(() => {
    setIsOpen(false);
    navigate('/dashboard');
    setTimeout(() => {
      scrollToElement('composer');
      focusElementById('command-input');
    }, 300);
  }, [navigate]);

  const checklistItems = useMemo(() => {
    return [
      {
        key: 'installApp',
        title: 'Install the Ekkos app',
        description: isAppInstalled
          ? 'The app is installed on this device.'
          : (
              <InstallAppInstructions
                platform={installPlatform}
                canPromptInstall={canPromptInstall}
                promptInstall={promptInstall}
              />
            ),
        status: isAppInstalled ? 'done' : 'todo',
      },
      {
        key: 'firstTaskScheduled',
        title: 'Schedule first task',
        description: state.firstTaskScheduled
          ? 'You already scheduled a reminder.'
          : 'Create your first reminder so Ekkos can take it from here.',
        status: state.firstTaskScheduled ? 'done' : 'todo',
        ctaLabel: state.firstTaskScheduled ? undefined : 'Open composer',
        onCta: state.firstTaskScheduled ? undefined : goToComposer,
      },
      {
        key: 'notificationsPermission',
        title: 'Enable notifications',
        description:
          notificationCopy.body ||
          'Allow notifications so reminders arrive on time.',
        status: state.notificationsPermission === 'granted'
          ? 'done'
          : notificationsBlocked
            ? 'blocked'
            : 'todo',
        ctaLabel:
          state.notificationsPermission === 'granted' || notificationsBlocked
            ? undefined
            : 'Enable now',
        onCta:
          state.notificationsPermission === 'granted' || notificationsBlocked
            ? undefined
            : handleEnableNotifications,
      },
      {
        key: 'notificationsDeviceRegistered',
        title: 'Register a device',
        description: state.notificationsDeviceRegistered
          ? 'You will receive reminders on this device.'
          : 'Register this device so push reminders can be delivered.',
        status: state.notificationsDeviceRegistered ? 'done' : 'todo',
        ctaLabel: state.notificationsDeviceRegistered ? undefined : 'Register device',
        onCta: state.notificationsDeviceRegistered
          ? undefined
          : () => {
              navigateToSettings('deliver-to-devices-card', 'register-device-button');
            },
      },
      {
        key: 'themeSelected',
        title: 'Pick your theme',
        description: state.themeSelected
          ? 'You chose your preferred theme.'
          : 'Choose light or dark to match your preference.',
        status: state.themeSelected ? 'done' : 'todo',
        ctaLabel: state.themeSelected ? undefined : 'Choose theme',
        onCta: state.themeSelected
          ? undefined
          : () => {
              navigateToSettings(undefined);
              setTimeout(() => {
                const toggle = document.querySelector('[aria-label="Toggle theme"]');
                toggle?.focus?.();
              }, 300);
            },
      },
      {
        key: 'personalizationSet',
        title: 'Set personalization',
        description: state.personalizationSet
          ? 'Retry mode, personality, and daytime preference are set.'
          : 'Pick retry mode, personality, and daytime preference.',
        status: state.personalizationSet ? 'done' : 'todo',
        ctaLabel: state.personalizationSet ? undefined : 'Open settings',
        onCta: state.personalizationSet
          ? undefined
          : () => {
              navigateToSettings('user-settings-card');
            },
      },
      // {
      //   key: 'multipleDevices',
      //   title: 'Add another device',
      //   description: state.multipleDevices
      //     ? 'Multiple devices registered — great redundancy!'
      //     : 'Register another device so reminders always find you.',
      //   status: state.multipleDevices ? 'done' : 'todo',
      //   ctaLabel: state.multipleDevices ? undefined : 'Add device',
      //   onCta: state.multipleDevices
      //     ? undefined
      //     : () => {
      //         navigateToSettings('deliver-to-devices-card', 'register-device-button');
      //       },
      // },
    ];
  }, [
    canPromptInstall,
    goToComposer,
    handleEnableNotifications,
    installPlatform,
    isAppInstalled,
    navigateToSettings,
    notificationCopy,
    notificationsBlocked,
    promptInstall,
    state.firstTaskScheduled,
    state.multipleDevices,
    state.notificationsDeviceRegistered,
    state.notificationsPermission,
    state.themeSelected,
    state.personalizationSet,
  ]);

  const totalItems = checklistItems.length || TOTAL_ITEMS;
  const completedCount = checklistItems.filter((item) => item.status === 'done').length;
  const hasBlocked = checklistItems.some((item) => item.status === 'blocked');
  const progressValue = (completedCount / totalItems) * 100;
  const ringColor =
    completedCount === totalItems
      ? successRingColor
      : hasBlocked
        ? warningRingColor
        : neutralRingColor;

  const summaryLabel =
    completedCount === totalItems ? 'All set' : `${completedCount}/${totalItems} ready`;

  const handleTriggerClick = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <>
      <Button
        variant="ghost"
        colorScheme="gray"
        size={buttonSize}
        px={buttonPaddingX}
        gap={buttonGap}
        onClick={handleTriggerClick}
        aria-label="Onboarding checklist"
        display="flex"
        alignItems="center"
        color="text.primary"
        _hover={{ bg: useColorModeValue('blackAlpha.50', 'whiteAlpha.100') }}
      >
        <CircularProgress
          size={ringSize}
          value={progressValue}
          color={ringColor}
          trackColor={ringTrackColor}
          thickness={ringThickness}
        >
          <CircularProgressLabel fontSize={ringLabelFontSize}>
            {completedCount}/{totalItems}
          </CircularProgressLabel>
        </CircularProgress>
        {!condensed && (
          <Box textAlign="left">
            <Text fontSize="xs" fontWeight="bold">
              {summaryLabel}
            </Text>
            <Text fontSize="xs" color="text.muted">
              {completedCount === totalItems ? 'Onboarding complete' : 'Finish onboarding'}
            </Text>
          </Box>
        )}
      </Button>

      <Drawer isOpen={isOpen} placement="right" onClose={() => setIsOpen(false)} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={popoverBg} borderColor={cardBorderColor} borderLeftWidth="1px">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={cardBorderColor}>
            <Text fontWeight="semibold">Onboarding checklist</Text>
            <Text fontSize="sm" color="gray.500">
              {completedCount === totalItems
                ? 'Everything is good to go. Nice work!'
                : 'Complete these items to get the best experience.'}
            </Text>
          </DrawerHeader>
          <DrawerBody py={4}>
            <VStack align="stretch" spacing={3}>
              {checklistItems.map((item) => {
                const { icon, color } = getStatusIcon(item.status);
                const isStringDescription = typeof item.description === 'string';
                return (
                  <Box
                    key={item.key}
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                    borderColor={item.status === 'done' ? doneRowBorder : todoRowBorder}
                    bg={item.status === 'done' ? doneRowBg : todoRowBg}
                  >
                    <HStack align="start" spacing={3} mb={2}>
                      <Icon as={icon} color={color} boxSize={4} mt={1} />
                      <Box flex="1">
                        <Text fontWeight="semibold" fontSize="sm">
                          {item.title}
                        </Text>
                        {isStringDescription ? (
                          <Text fontSize="xs" color="gray.500">
                            {item.description}
                          </Text>
                        ) : (
                          <Box mt={2}>{item.description}</Box>
                        )}
                      </Box>
                      {item.ctaLabel && item.onCta && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={item.onCta}
                          isLoading={ctaLoadingKey === item.key}
                          isDisabled={ctaLoadingKey && ctaLoadingKey !== item.key}
                        >
                          {item.ctaLabel}
                        </Button>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
